import React, { useState } from "react";
import styled from "styled-components";
import emailjs from "emailjs-com";

const FormSection = styled.section`
  margin: 4rem 0;
  padding: 3rem;
  background: #1a1a1a;
  border-radius: 15px;
  border: 1px solid #333;
`;

const FormTitle = styled.h2`
  text-align: center;
  color: #ff6600;
  margin-bottom: 2rem;
  font-size: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #e0e0e0;
  }

  input,
  select,
  textarea {
    padding: 12px 15px;
    border: 2px solid #333;
    background: #0a0a0a;
    color: #e0e0e0;
    border-radius: 8px;
    font-size: 16px;
    transition: border-color 0.3s ease;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: #ff6600;
      box-shadow: 0 0 0 3px rgba(255, 102, 0, 0.08);
    }

    &.error {
      border-color: #cc5200;
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }

  .error-message {
    color: #cc5200;
    font-size: 14px;
    margin-top: 0.5rem;
  }
`;

const SubmitButton = styled.button`
  background: #ff6600;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover:not(:disabled) {
    background: #cc5200;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 102, 0, 0.18);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const InfoSection = styled.div`
  .info-card {
    background: white;
    padding: 2rem;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;

    h3 {
      color: #ff6600;
      margin-bottom: 1rem;
    }

    p {
      color: #666;
      line-height: 1.6;
      margin-bottom: 1rem;
    }

    ul {
      list-style: none;
      padding: 0;

      li {
        padding: 0.5rem 0;
        border-bottom: 1px solid #f0f0f0;
        color: #666;

        &:last-child {
          border-bottom: none;
        }

        &:before {
          content: "✓";
          color: #ff6600;
          font-weight: bold;
          margin-right: 0.5rem;
        }
      }
    }
  }

  .contact-info {
    background: #ff6600;
    color: white;
    padding: 2rem;
    border-radius: 10px;
    text-align: center;

    h3 {
      color: white;
      margin-bottom: 1rem;
    }

    .contact-item {
      margin: 1rem 0;

      a {
        color: white;
        font-weight: 500;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const TyreQuoteForm = ({ tyreType }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    tyreSize: "",
    quantity: "",
    vehicleType: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.tyreSize.trim()) {
      newErrors.tyreSize = "Tyre size is required";
    }

    if (!formData.quantity.trim()) {
      newErrors.quantity = "Quantity is required";
    }

    if (!formData.vehicleType.trim()) {
      newErrors.vehicleType = "Vehicle type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const templateParams = {
        to_email: "contact@roadwisetyres.com.au",
        tyre_type: tyreType,
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        company: formData.company,
        tyre_size: formData.tyreSize,
        quantity: formData.quantity,
        vehicle_type: formData.vehicleType,
        message: formData.message,
        subject: `${tyreType} Tyre Quote Request from ${formData.name}`,
      };

      const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const USER_ID = process.env.REACT_APP_EMAILJS_USER_ID || process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (SERVICE_ID && TEMPLATE_ID && USER_ID) {
        // send via EmailJS
        await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, USER_ID);
      } else {
        // fallback: log payload for manual handling
        console.log("EmailJS not configured — payload:", templateParams);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        tyreSize: "",
        quantity: "",
        vehicleType: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Failed to send message. Please try again or contact us directly.");
    }

    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <FormSection>
        <div>
          <SuccessMessage>
            <h3>Thank you for your quote request!</h3>
            <p>We have received your {tyreType} tyre quote request and will get back to you within 24 hours.</p>
            <button
              className="btn"
              onClick={() => setIsSubmitted(false)}
              style={{ marginTop: "1rem" }}>
              Submit Another Quote
            </button>
          </SuccessMessage>
        </div>
      </FormSection>
    );
  }

  return (
    <FormSection>
      <FormTitle>Get a Quote for {tyreType} Tyres</FormTitle>

      <FormGrid>
        <div>
          <FormContainer onSubmit={handleSubmit}>
            <FormGroup>
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? "error" : ""}
                placeholder="Enter your full name"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </FormGroup>

            <FormGroup>
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? "error" : ""}
                placeholder="Enter your email address"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </FormGroup>

            <FormGroup>
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={errors.phone ? "error" : ""}
                placeholder="Enter your phone number"
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </FormGroup>

            <FormGroup>
              <label htmlFor="company">Company Name</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Enter your company name (optional)"
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="tyreSize">Tyre Size *</label>
              <select
                id="tyreSize"
                name="tyreSize"
                value={formData.tyreSize}
                onChange={handleChange}
                className={errors.tyreSize ? "error" : ""}>
                <option value="">Select tyre size</option>
                <option value="11R22.5">11R22.5</option>
                <option value="295/80R22.5">295/80R22.5</option>
                <option value="275/70R22.5">275/70R22.5</option>
                <option value="295">295 (Steer)</option>
                <option value="385">385 (Steer)</option>
                <option value="11R">11R (Drive/All/Trailer)</option>
                <option value="Other">Other (specify in message)</option>
              </select>
              {errors.tyreSize && <span className="error-message">{errors.tyreSize}</span>}
            </FormGroup>

            <FormGroup>
              <label htmlFor="quantity">Quantity *</label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className={errors.quantity ? "error" : ""}
                placeholder="Number of tyres needed"
                min="1"
              />
              {errors.quantity && <span className="error-message">{errors.quantity}</span>}
            </FormGroup>

            <FormGroup>
              <label htmlFor="vehicleType">Vehicle Type *</label>
              <select
                id="vehicleType"
                name="vehicleType"
                value={formData.vehicleType}
                onChange={handleChange}
                className={errors.vehicleType ? "error" : ""}>
                <option value="">Select vehicle type</option>
                <option value="Truck">Truck</option>
                {/* focus on heavy vehicles only */}
                <option value="B-Double">B-Double</option>
                <option value="Road Train">Road Train</option>
                <option value="Other">Other</option>
              </select>
              {errors.vehicleType && <span className="error-message">{errors.vehicleType}</span>}
            </FormGroup>

            <FormGroup>
              <label htmlFor="message">Additional Requirements</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Please provide any additional information about your requirements..."
              />
            </FormGroup>

            <SubmitButton
              type="submit"
              disabled={isSubmitting}>
              {isSubmitting ? "Sending Quote Request..." : "Get Quote"}
            </SubmitButton>
          </FormContainer>
        </div>

        <div>
          <InfoSection>
            <div className="info-card">
              <h3>Why Choose {tyreType} Tyres?</h3>
              <ul>
                <li>Premium quality construction</li>
                <li>Superior durability and longevity</li>
                <li>Excellent performance in all conditions</li>
                <li>Competitive pricing</li>
                <li>Professional fitting service</li>
                <li>Warranty protection</li>
              </ul>
            </div>

            <div className="contact-info">
              <h3>Need Immediate Assistance?</h3>
              <div className="contact-item">
                <strong>Call us directly:</strong>
                <br />
                <a href="tel:0481313403">0481 313 403</a>
              </div>
              <div className="contact-item">
                <strong>Email us:</strong>
                <br />
                <a href="mailto:contact@roadwisetyres.com.au">contact@roadwisetyres.com.au</a>
              </div>
              <p>Our experts are available 24/7 to help you find the perfect tyres for your needs.</p>
            </div>
          </InfoSection>
        </div>
      </FormGrid>
    </FormSection>
  );
};

export default TyreQuoteForm;
